import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Upload as UploadIcon, Palette, Loader2, Mail, Bell, BellOff, ShieldCheck, AlertTriangle, Copy } from "lucide-react";
import { pushSupported, getPushStatus, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export default function Branding() {
  const [form, setForm] = useState({
    tagline: "", license_numbers: [], address_full: "", contact_name: "",
    contact_phone: "", contact_email: "", primary_color: "#00E5FF",
  });
  const [licenseInput, setLicenseInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoTick, setLogoTick] = useState(0);
  const [notif, setNotif] = useState({ enabled: false, hour_utc: 13, warn_days: [7, 2] });
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get("/branding").then(r => setForm(v => ({ ...v, ...r.data }))).catch(() => {});
    api.get("/notifications/settings").then(r => setNotif(v => ({ ...v, ...r.data }))).catch(() => {});
    api.get("/email/domain-status").then(r => setDomainStatus(r.data)).catch(() => {});
    if (pushSupported()) getPushStatus().then(setPushStatus).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding", form);
      toast.success("Branding saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveNotif = async () => {
    try { await api.put("/notifications/settings", notif); toast.success("Notification settings saved"); } catch { toast.error("Failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      setLogoTick(t => t + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/notifications/test-email", { to: testEmail || null });
      toast.success(`Sent to ${r.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingTest(false); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush();
        toast.success("Push notifications disabled");
      } else {
        await subscribeToPush();
        toast.success("Push notifications enabled — you'll be alerted when opportunities are due soon");
      }
      setPushStatus(await getPushStatus());
    } catch (e) {
      toast.error(e?.message || "Could not toggle push notifications");
    } finally { setPushBusy(false); }
  };

  const sendPushTest = async () => {
    setPushBusy(true);
    try {
      const r = await api.post("/push/test");
      if (r.data.sent > 0) toast.success(`Test push delivered (${r.data.sent})`);
      else toast.error(r.data.errors?.[0] || "No push devices received the alert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send test push"); }
    finally { setPushBusy(false); }
  };

  const sendDeadlinePush = async () => {
    setPushBusy(true);
    try {
      const r = await api.post("/push/send-deadline-alerts");
      toast.success(`Alerted ${r.data.sent}/${r.data.total_subscriptions} device(s) about ${r.data.upcoming} upcoming deadline(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPushBusy(false); }
  };

  const sendBulk = async () => {
    setBulkSending(true);
    try {
      const r = await api.post("/notifications/send-bulk-reminder", {});
      toast.success(`Digest sent to ${r.data.sent.length} recipient(s)${r.data.failed.length ? ` · ${r.data.failed.length} failed` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send digest"); }
    finally { setBulkSending(false); }
  };

  const addLicense = () => {
    if (!licenseInput) return;
    setForm({ ...form, license_numbers: [...form.license_numbers, licenseInput] });
    setLicenseInput("");
  };

  const token = localStorage.getItem("bf_token");
  const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo?auth=${encodeURIComponent(token)}&t=${logoTick}`;

  return (
    <div className="p-8 stagger-in max-w-5xl" data-testid="branding-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// WORKSPACE BRANDING</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Brand & Notifications</h1>
        <p className="text-slate-400 mt-2">Every proposal DOCX includes your logo, contact info, and license numbers.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-1 bg-[#0D1424] border border-white/10 p-6">
          <div className="mono-label text-cyan-400 mb-4 flex items-center gap-2"><Palette className="w-3.5 h-3.5" />LOGO</div>
          <div className="border border-white/10 aspect-video flex items-center justify-center bg-black/40 mb-4">
            <img key={logoTick} src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          </div>
          <input ref={logoRef} type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" data-testid="logo-input" />
          <button onClick={() => logoRef.current?.click()} disabled={uploading} className="w-full border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 px-4 py-2 mono-label flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upload-logo-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadIcon className="w-4 h-4" />}{uploading ? "UPLOADING" : "UPLOAD LOGO"}
          </button>
          <div className="text-xs text-slate-500 mt-3">PNG, JPG, WEBP, SVG · max 5MB</div>
        </div>

        <div className="md:col-span-2 bg-[#0D1424] border border-white/10 p-6 space-y-4">
          <div className="mono-label text-cyan-400 mb-2">COMPANY DETAILS</div>
          <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} placeholder="Building excellence since 1985" testid="tagline" />
          <Field label="Full Address" value={form.address_full} onChange={(v) => setForm({ ...form, address_full: v })} placeholder="18911 Hardy Oak Blvd, San Antonio, TX 78232" testid="address" />
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Contact Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="contact-name" />
            <Field label="Contact Email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="contact-email" />
            <Field label="Contact Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="contact-phone" />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="UEI" value={form.uei || ""} onChange={(v) => setForm({ ...form, uei: v })} placeholder="12 char UEI" testid="uei" />
            <Field label="CAGE Code" value={form.cage_code || ""} onChange={(v) => setForm({ ...form, cage_code: v })} placeholder="5 char CAGE" testid="cage" />
            <Field label="DUNS" value={form.duns || ""} onChange={(v) => setForm({ ...form, duns: v })} placeholder="9 digit DUNS" testid="duns" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Signatory Name" value={form.signatory_name || ""} onChange={(v) => setForm({ ...form, signatory_name: v })} testid="signatory-name" />
            <Field label="Signatory Title" value={form.signatory_title || ""} onChange={(v) => setForm({ ...form, signatory_title: v })} placeholder="President" testid="signatory-title" />
          </div>
          <div>
            <div className="mono-label text-slate-400 mb-2">License Numbers</div>
            <div className="flex gap-2 mb-2">
              <input value={licenseInput} onChange={(e) => setLicenseInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLicense())} placeholder="e.g. TX GC 12345" className="flex-1 bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="license-input" />
              <button onClick={addLicense} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-3 mono-label" data-testid="license-add">ADD</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.license_numbers.map((lic, i) => (
                <span key={i} className="mono-label text-cyan-400 border border-cyan-400/30 px-2 py-1 flex items-center gap-1.5">{lic}
                  <button onClick={() => setForm({ ...form, license_numbers: form.license_numbers.filter((_, idx) => idx !== i) })} className="hover:text-red-400">×</button>
                </span>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2" data-testid="save-branding"><Save className="w-4 h-4" />{saving ? "SAVING" : "SAVE BRANDING"}</button>
        </div>
      </div>

      <div className="bg-[#0D1424] border border-white/10 p-6" data-testid="notif-panel">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><Mail className="w-3.5 h-3.5" />DEADLINE EMAIL NOTIFICATIONS</div>
            <div className="font-display font-bold text-xl">Never miss a deadline</div>
            <p className="text-slate-400 text-sm mt-1">Get an email digest of opportunities due within 7 and 2 days.</p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={notif.enabled} onChange={(e) => setNotif({ ...notif, enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="notif-enabled" />
            <span className="mono-label text-slate-300">DIGEST ENABLED</span>
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com (optional)" className="flex-1 min-w-[200px] bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="test-email-input" />
          <button onClick={sendTest} disabled={sendingTest} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2" data-testid="send-test-email">
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}SEND TEST EMAIL
          </button>
          <button onClick={saveNotif} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-5 py-2.5 mono-label" data-testid="save-notif">SAVE SETTINGS</button>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-6">
          <div>
            <div className="mono-label text-slate-400 mb-1.5">CUSTOM SENDER EMAIL (optional)</div>
            <input type="email" value={form.sender_email || ""} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="notifications@yourdomain.com" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="sender-email" />
            <div className="text-xs text-slate-500 mt-1.5">Must be from a Resend-verified domain, otherwise sending will fail. Leave blank to use the default sender.</div>
          </div>
          <div>
            <div className="mono-label text-slate-400 mb-1.5">RECIPIENT EMAILS FOR DIGEST</div>
            <input type="text" value={(form.reminder_recipients || []).join(", ")} onChange={(e) => setForm({ ...form, reminder_recipients: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} placeholder="pm@yourco.com, super@yourco.com" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="reminder-recipients" />
            <div className="text-xs text-slate-500 mt-1.5">Comma-separated. Used by "Send digest to everyone" below.</div>
          </div>
        </div>
        <button onClick={sendBulk} disabled={bulkSending} className="mt-3 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 px-5 py-2.5 mono-label disabled:opacity-50 flex items-center gap-2" data-testid="send-bulk-reminder">
          {bulkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}SEND DIGEST TO EVERYONE
        </button>

        <div className="mt-3 text-xs text-slate-500">
          <strong className="text-slate-400">Note:</strong> Resend sandbox mode only sends to the email that owns the API key. Verify a domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300">resend.com/domains</a> to send to team members.
        </div>
      </div>

      <div className="bg-[#0D1424] border border-white/10 p-6 mt-6" data-testid="domain-panel">
        <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" />EMAIL DOMAIN STATUS</div>
        <div className="font-display font-bold text-xl mb-3">{domainStatus?.verified ? "Custom domain verified" : "Sender: sandbox mode"}</div>
        {!domainStatus && <div className="text-slate-500 text-sm">Checking domain status…</div>}
        {domainStatus && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="border border-white/10 p-3">
                <div className="mono-label text-slate-500 mb-1">CURRENT SENDER</div>
                <div className="font-mono text-slate-200 break-all" data-testid="domain-sender">{domainStatus.sender || "—"}</div>
              </div>
              <div className="border border-white/10 p-3">
                <div className="mono-label text-slate-500 mb-1">MODE</div>
                <div className={`font-mono ${domainStatus.is_sandbox ? "text-amber-400" : "text-emerald-400"}`} data-testid="domain-mode">
                  {domainStatus.is_sandbox ? "SANDBOX" : "PRODUCTION"}
                </div>
              </div>
              <div className="border border-white/10 p-3">
                <div className="mono-label text-slate-500 mb-1">DOMAIN VERIFIED</div>
                <div className={`font-mono ${domainStatus.verified ? "text-emerald-400" : "text-amber-400"}`} data-testid="domain-verified">
                  {domainStatus.verified === true ? "YES" : domainStatus.verified === false ? "PENDING" : "N/A"}
                </div>
              </div>
            </div>
            {domainStatus.is_sandbox && (
              <div className="border border-amber-400/40 bg-amber-400/5 p-4 text-sm text-slate-200 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-300 mb-1">You're on the Resend sandbox sender</div>
                  <div className="text-slate-300">To send deadline emails to your whole team, verify your custom domain (e.g. <span className="font-mono text-cyan-300">chiefconstuctionco.com</span>) at{" "}
                    <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">resend.com/domains</a>. Then update <span className="font-mono">SENDER_EMAIL</span> in the backend env and restart. Until then, emails only reach the address that created the Resend API key.
                  </div>
                </div>
              </div>
            )}
            {domainStatus.domains?.length > 0 && (
              <div className="border border-white/10 p-3">
                <div className="mono-label text-slate-400 mb-2">DOMAINS ON RESEND ACCOUNT</div>
                <div className="space-y-1.5">
                  {domainStatus.domains.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className="font-mono text-slate-200">{d.name}</span>
                      <span className={`mono-label ${String(d.status).toLowerCase() === "verified" ? "text-emerald-400" : "text-amber-400"}`}>{String(d.status || "").toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#0D1424] border border-white/10 p-6 mt-6" data-testid="push-panel">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><Bell className="w-3.5 h-3.5" />BROWSER PUSH DEADLINE ALERTS</div>
            <div className="font-display font-bold text-xl">Ping this browser when a bid is due</div>
            <p className="text-slate-400 text-sm mt-1">Enable native browser notifications for opportunities inside the next 7 days — even when BidForge is in another tab.</p>
          </div>
          <div className="mono-label">
            <span className={`px-2 py-1 border ${pushStatus.subscribed ? "border-emerald-400 text-emerald-400" : "border-white/20 text-slate-400"}`} data-testid="push-status">
              {pushStatus.supported ? (pushStatus.subscribed ? "ENABLED" : (pushStatus.permission === "denied" ? "BLOCKED" : "OFF")) : "UNSUPPORTED"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={togglePush} disabled={!pushStatus.supported || pushBusy} className={`px-5 py-2.5 mono-label flex items-center gap-2 disabled:opacity-50 ${pushStatus.subscribed ? "border border-white/20 hover:border-red-400 hover:text-red-400" : "bg-cyan-400 text-[#070B14] hover:bg-cyan-300"}`} data-testid="toggle-push">
            {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : pushStatus.subscribed ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            {pushStatus.subscribed ? "DISABLE PUSH" : "ENABLE PUSH"}
          </button>
          <button onClick={sendPushTest} disabled={!pushStatus.subscribed || pushBusy} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-5 py-2.5 mono-label disabled:opacity-40" data-testid="send-push-test">SEND TEST PUSH</button>
          <button onClick={sendDeadlinePush} disabled={!pushStatus.subscribed || pushBusy} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-5 py-2.5 mono-label disabled:opacity-40" data-testid="send-push-deadlines">PUSH DEADLINE RADAR</button>
        </div>
        {!pushStatus.supported && <div className="text-xs text-slate-500 mt-3">This browser (or context) does not support the Web Push API. Try Chrome, Edge, or Firefox on desktop.</div>}
        {pushStatus.supported && pushStatus.permission === "denied" && <div className="text-xs text-amber-400 mt-3">Notifications are blocked in your browser settings. Re-allow them for this site to enable push.</div>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="mono-label text-slate-400 mb-2">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid={testid} />
    </div>
  );
}
